import { useNavigate } from '@tanstack/react-router';
import { ArrowRight, CheckCircle2, Fingerprint, KeyRound, Sparkles, Users } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Aurora } from '../components/feedback/Beacon';
import { LanguageToggle } from '../components/shell/LanguageToggle';
import { Button } from '../components/ui/Button';
import { TextField } from '../components/ui/Field';
import { LogoMark, Wordmark } from '../components/ui/LogoMark';
import { OtpInput } from '../components/ui/OtpInput';
import { ThemeSwitcher } from '../theme/ThemeSwitcher';
import { toast } from '../components/feedback/toast';
import { isBreached } from '../lib/breached';
import {
  authErrorKey,
  completeReset,
  sendResetCode,
  signInWithPasskey,
  signInWithPassword,
  submitNewPassword,
  submitTotp,
  type NextStep,
} from './cognito';
import { useSession } from './session';

type Step = 'credentials' | 'newPassword' | 'totp' | 'forgot' | 'resetPassword';

const slide = {
  initial: { opacity: 0, x: 24, filter: 'blur(6px)' },
  animate: { opacity: 1, x: 0, filter: 'blur(0px)' },
  exit: { opacity: 0, x: -24, filter: 'blur(6px)' },
  transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] as const },
};

export function SignInPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { markSignedIn } = useSession();
  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'password' | 'passkey' | 'next' | null>(null);

  const advance = (nextStep: NextStep) => {
    setError(null);
    if (nextStep === 'done') {
      markSignedIn();
      void navigate({ to: '/' });
    } else if (nextStep === 'newPassword') setStep('newPassword');
    else if (nextStep === 'totp') {
      setCode('');
      setStep('totp');
    } else if (nextStep === 'resetPassword') startReset();
    else setError(t('auth.errors.generic'));
  };

  /** Code + new password: after an admin unlock, or "forgot password". */
  const startReset = () => {
    setCode('');
    setNewPassword('');
    setConfirm('');
    setStep('resetPassword');
  };

  /** Length, match, and not in a known breach. Returns an error message or null. */
  const checkNewPassword = async (): Promise<string | null> => {
    if (newPassword.length < 14) return t('auth.newPassword.tooShort');
    if (newPassword !== confirm) return t('auth.newPassword.mismatch');
    if (await isBreached(newPassword)) return t('auth.errors.breached');
    return null;
  };

  /** Runs an auth action; a returned step advances the flow, `null` means it was handled. */
  const run = async (
    kind: 'password' | 'passkey' | 'next',
    action: () => Promise<NextStep | null>,
  ) => {
    setBusy(kind);
    setError(null);
    try {
      const nextStep = await action();
      if (nextStep) advance(nextStep);
    } catch (err) {
      if ((err as Error).name === 'LocalValidation') {
        setError((err as Error).message);
        return;
      }
      const key = authErrorKey(err);
      // Cognito may report a required reset as an error rather than a next step.
      if (key === 'auth.errors.resetRequired') startReset();
      else if (key === 'auth.errors.sessionExpired' && step !== 'credentials') {
        // The sign-in session (password → code) expired: retrying the code can't work, so
        // start over instead of leaving the person stuck on a step that will keep failing.
        setStep('credentials');
        setPassword('');
        setCode('');
        setError(t('auth.codeExpired'));
      } else {
        if (key === 'auth.errors.codeMismatch') setCode('');
        setError(t(key));
      }
    } finally {
      setBusy(null);
    }
  };

  const onCredentials = (e: FormEvent) => {
    e.preventDefault();
    void run('password', () => signInWithPassword(email.trim(), password));
  };

  const onNewPassword = (e: FormEvent) => {
    e.preventDefault();
    void run('next', async () => {
      const problem = await checkNewPassword();
      if (problem) throw Object.assign(new Error(problem), { name: 'LocalValidation' });
      return submitNewPassword(newPassword);
    });
  };

  const onForgot = (e: FormEvent) => {
    e.preventDefault();
    void run('next', async () => {
      await sendResetCode(email.trim());
      return 'resetPassword';
    });
  };

  const onReset = (e: FormEvent) => {
    e.preventDefault();
    void run('next', async () => {
      const problem = await checkNewPassword();
      if (problem) throw Object.assign(new Error(problem), { name: 'LocalValidation' });
      await completeReset(email.trim(), code, newPassword);
      toast.success(t('auth.reset.done'));
      setPassword('');
      setStep('credentials');
      return null;
    });
  };

  const onTotp = (value: string) => void run('next', () => submitTotp(value));

  return (
    <div className="relative grid min-h-dvh bg-bg lg:grid-cols-[1.15fr_1fr]">
      <Hero />

      <main className="relative flex flex-col items-center justify-center overflow-hidden px-5 py-20">
        <div className="lg:hidden">
          <Aurora intensity="soft" />
        </div>
        {/* Soft light pooling behind the card on desktop, so the form side isn't flat. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-1/2 hidden h-[640px] w-[640px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-50 blur-3xl lg:block"
          style={{
            background:
              'radial-gradient(circle at 40% 40%, color-mix(in oklab, var(--glow-1) 55%, transparent), transparent 60%), radial-gradient(circle at 65% 65%, color-mix(in oklab, var(--glow-2) 45%, transparent), transparent 55%)',
          }}
        />
        <div className="absolute right-4 top-4 z-10 flex items-center gap-2 sm:right-6 sm:top-6">
          <LanguageToggle id="signin-lang" />
          <ThemeSwitcher />
        </div>

        <div className="relative mb-8 flex items-center gap-3 lg:hidden">
          <LogoMark size={40} draw />
          <Wordmark className="text-2xl" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 18, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="glass relative w-full max-w-[420px] rounded-xl p-7 elevated sm:p-9"
        >
          <AnimatePresence mode="wait" initial={false}>
            {step === 'credentials' && (
              <motion.form
                key="credentials"
                {...slide}
                onSubmit={onCredentials}
                className="flex flex-col gap-5"
              >
                <Header
                  icon={<Sparkles size={18} />}
                  title={t('auth.signIn.title')}
                  subtitle={t('auth.signIn.subtitle')}
                />
                <TextField
                  label={t('auth.signIn.email')}
                  type="email"
                  autoComplete="username webauthn"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <TextField
                  label={t('auth.signIn.password')}
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => {
                    setError(null);
                    setStep('forgot');
                  }}
                  className="-mt-3 self-end text-sm font-semibold text-primary underline-offset-4 hover:underline"
                >
                  {t('auth.signIn.forgot')}
                </button>
                <ErrorLine error={error} />
                <Button
                  type="submit"
                  size="lg"
                  loading={busy === 'password'}
                  icon={<ArrowRight size={18} />}
                >
                  {t('auth.signIn.submit')}
                </Button>
                <Divider label={t('auth.signIn.or')} />
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  loading={busy === 'passkey'}
                  icon={<Fingerprint size={18} />}
                  disabled={!email.includes('@')}
                  onClick={() => void run('passkey', () => signInWithPasskey(email.trim()))}
                >
                  {t('auth.signIn.passkey')}
                </Button>
                <p className="text-center text-xs leading-relaxed text-muted">
                  {t('auth.signIn.invited')}
                </p>
              </motion.form>
            )}

            {step === 'newPassword' && (
              <motion.form
                key="newPassword"
                {...slide}
                onSubmit={onNewPassword}
                className="flex flex-col gap-5"
              >
                <Header
                  icon={<KeyRound size={18} />}
                  title={t('auth.newPassword.title')}
                  subtitle={t('auth.newPassword.subtitle')}
                />
                <TextField
                  label={t('auth.newPassword.password')}
                  type="password"
                  autoComplete="new-password"
                  hint={t('auth.newPassword.rule')}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <StrengthBar length={newPassword.length} />
                <TextField
                  label={t('auth.newPassword.confirm')}
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
                <ErrorLine error={error} />
                <Button type="submit" size="lg" loading={busy === 'next'}>
                  {t('auth.newPassword.submit')}
                </Button>
              </motion.form>
            )}

            {step === 'totp' && (
              <motion.div key="totp" {...slide} className="flex flex-col gap-6">
                <Header
                  icon={<KeyRound size={18} />}
                  title={t('auth.totp.title')}
                  subtitle={t('auth.totp.subtitle')}
                />
                <OtpInput
                  label={t('auth.totp.title')}
                  value={code}
                  onChange={setCode}
                  onComplete={onTotp}
                  error={Boolean(error)}
                  disabled={busy === 'next'}
                />
                <ErrorLine error={error} />
                <Button
                  size="lg"
                  loading={busy === 'next'}
                  disabled={code.length < 6}
                  onClick={() => onTotp(code)}
                >
                  {t('auth.totp.submit')}
                </Button>
              </motion.div>
            )}
            {step === 'forgot' && (
              <motion.form
                key="forgot"
                {...slide}
                onSubmit={onForgot}
                className="flex flex-col gap-5"
              >
                <Header
                  icon={<KeyRound size={18} />}
                  title={t('auth.forgot.title')}
                  subtitle={t('auth.forgot.subtitle')}
                />
                <TextField
                  label={t('auth.signIn.email')}
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <ErrorLine error={error} />
                <Button type="submit" size="lg" loading={busy === 'next'}>
                  {t('auth.forgot.send')}
                </Button>
                <BackLink onClick={() => setStep('credentials')} label={t('auth.forgot.back')} />
              </motion.form>
            )}

            {step === 'resetPassword' && (
              <motion.form
                key="reset"
                {...slide}
                onSubmit={onReset}
                className="flex flex-col gap-5"
              >
                <Header
                  icon={<KeyRound size={18} />}
                  title={t('auth.reset.title')}
                  subtitle={t('auth.reset.subtitle', { email: email.trim() })}
                />
                <OtpInput
                  label={t('auth.reset.code')}
                  value={code}
                  onChange={setCode}
                  error={Boolean(error)}
                />
                <TextField
                  label={t('auth.newPassword.password')}
                  type="password"
                  autoComplete="new-password"
                  hint={t('auth.newPassword.rule')}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <StrengthBar length={newPassword.length} />
                <TextField
                  label={t('auth.newPassword.confirm')}
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
                <ErrorLine error={error} />
                <Button
                  type="submit"
                  size="lg"
                  loading={busy === 'next'}
                  disabled={code.length < 6}
                >
                  {t('auth.reset.submit')}
                </Button>
                <button
                  type="button"
                  className="text-sm font-semibold text-primary underline-offset-4 hover:underline"
                  onClick={() =>
                    void sendResetCode(email.trim())
                      .then(() => toast.success(t('auth.reset.resent', { email: email.trim() })))
                      .catch((err: unknown) => setError(t(authErrorKey(err))))
                  }
                >
                  {t('auth.reset.resend')}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>
      </main>
    </div>
  );
}

function BackLink({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-center text-sm font-semibold text-muted underline-offset-4 hover:text-text hover:underline"
    >
      ← {label}
    </button>
  );
}

function Header({
  icon,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}) {
  return (
    <div>
      <div className="mb-4 grid h-10 w-10 place-items-center rounded-md bg-primary text-on-primary shadow-[0_10px_24px_-10px_var(--primary)]">
        {icon}
      </div>
      <h1 className="text-2xl font-extrabold text-text">{title}</h1>
      <p className="mt-1 text-base text-muted">{subtitle}</p>
    </div>
  );
}

function ErrorLine({ error }: { error: string | null }) {
  return (
    <AnimatePresence>
      {error && (
        <motion.p
          role="alert"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="rounded-md border border-danger/30 bg-danger/8 px-3 py-2 text-sm font-semibold text-danger"
        >
          {error}
        </motion.p>
      )}
    </AnimatePresence>
  );
}

function Divider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-widest text-muted">
      <span className="h-px flex-1 bg-border" />
      {label}
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

/** Length-based meter: long passphrases are what matter (NIST 800-63B). */
function StrengthBar({ length }: { length: number }) {
  const ratio = Math.min(length / 20, 1);
  const color = length >= 14 ? 'var(--success)' : length >= 8 ? 'var(--warning)' : 'var(--danger)';
  return (
    <div className="-mt-2 h-1.5 overflow-hidden rounded-full bg-subtle" aria-hidden="true">
      <motion.div
        className="h-full rounded-full"
        animate={{ width: `${ratio * 100}%`, backgroundColor: color }}
        transition={{ type: 'spring', stiffness: 200, damping: 26 }}
      />
    </div>
  );
}

// ── Hero: the left half on desktop ──────────────────────────────────────────────────────────

function Hero() {
  const { t } = useTranslation();
  const lines = t('auth.hero', { returnObjects: true }) as string[];
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setIndex((i) => (i + 1) % lines.length), 4200);
    return () => clearInterval(timer);
  }, [lines.length]);

  return (
    <aside className="relative hidden overflow-hidden border-r border-border lg:flex lg:flex-col lg:justify-between lg:p-12">
      <Aurora />
      <div className="pointer-events-none absolute -left-24 top-24 h-[120vh] w-[120vh]">
        <div
          className="absolute left-0 top-0 h-full w-[60%] origin-top-left animate-beam"
          style={{
            background:
              'conic-gradient(from 100deg at 0% 0%, transparent 0deg, color-mix(in oklab, var(--accent) 30%, transparent) 14deg, transparent 30deg)',
            filter: 'blur(14px)',
          }}
        />
      </div>

      <div className="relative flex items-center gap-3">
        <LogoMark size={44} draw />
        <Wordmark className="text-2xl" />
      </div>

      <div className="relative max-w-xl">
        <AnimatePresence mode="wait">
          <motion.h2
            key={index}
            className="font-display text-4xl font-extrabold leading-[1.05] text-text xl:text-[3.6rem]"
            initial="hidden"
            animate="show"
            exit="exit"
          >
            {(lines[index] ?? '').split(' ').map((word, i) => (
              <motion.span
                key={`${word}-${i}`}
                className={`mr-[0.25em] inline-block ${i % 4 === 1 ? 'text-gradient' : ''}`}
                variants={{
                  hidden: { opacity: 0, y: 24, rotateX: -60 },
                  show: {
                    opacity: 1,
                    y: 0,
                    rotateX: 0,
                    transition: { delay: i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] },
                  },
                  exit: { opacity: 0, y: -16, transition: { duration: 0.25, delay: i * 0.02 } },
                }}
              >
                {word}
              </motion.span>
            ))}
          </motion.h2>
        </AnimatePresence>
        <p className="mt-6 text-md text-muted">{t('app.tagline')}</p>
      </div>

      <FloatingCards />
    </aside>
  );
}

/** Glimpses of the product drifting gently: what agencies get, at a glance. */
function FloatingCards() {
  const { t } = useTranslation();
  const cards = [
    { key: 'visit', icon: <CheckCircle2 size={16} className="text-success" />, x: 0, delay: 0.2 },
    { key: 'note', icon: <Sparkles size={16} className="text-accent" />, x: 120, delay: 0.45 },
    { key: 'team', icon: <Users size={16} className="text-info" />, x: 40, delay: 0.7 },
  ];
  return (
    <div className="relative h-52" aria-hidden="true">
      {cards.map((c, i) => (
        <motion.div
          key={c.key}
          className="glass absolute flex w-64 items-center gap-3 rounded-lg px-4 py-3 elevated"
          style={{ left: c.x, top: i * 64 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: [0, -6, 0] }}
          transition={{
            opacity: { delay: c.delay },
            y: { duration: 5 + i, repeat: Infinity, ease: 'easeInOut', delay: c.delay },
          }}
        >
          <span className="grid h-8 w-8 place-items-center rounded-full bg-subtle">{c.icon}</span>
          <div>
            <p className="text-sm font-bold text-text">{t(`auth.preview.${c.key}Title`)}</p>
            <p className="text-xs text-muted">{t(`auth.preview.${c.key}Body`)}</p>
          </div>
        </motion.div>
      ))}
    </div>
  );
}
